import { useSendNotification } from "@app/hooks/useNotification";
import { getBaseUrl } from "@app/lib/api/config";
import { clientFetch } from "@app/lib/egress/client";
import { emptyArray, useFetcher, useSWRWithDefaults } from "@app/lib/swr/swr";
import type { FrameLiveSheet } from "@app/types/assistant/visualization";
import type {
  LiveDocAccess,
  LiveDocConnectionType,
  LiveDocKind,
  LiveDocMemberType,
  LiveDocSummaryType,
  LiveDocType,
  LiveHuddleType,
} from "@app/types/live_docs";
import { isHuddleWritingNotes } from "@app/types/live_docs";
import type { LightWorkspaceType } from "@app/types/user";
import type {
  BrowserFramesResponse,
  BrowserVideoResponse,
  HistoryResponse,
  MediaTokenResponse,
  SheetReadResponse,
  VersionResponse,
} from "@memo-air/protocol";
import type {
  LiveDockItem,
  LiveHostDoc,
  LiveHostRunner,
} from "@memo-air/react";
import { useCallback, useMemo } from "react";
import type { Fetcher } from "swr";
import { z } from "zod";

type GetLiveDocTokenResponseBody = LiveDocConnectionType & {
  liveDoc: LiveDocType;
  canShare: boolean;
  canOpenConversation: boolean;
};

type GetLiveDocsResponseBody = {
  liveDocs: LiveDocType[];
  summaries?: Record<string, LiveDocSummaryType>;
};

const emptySummaries: Record<string, LiveDocSummaryType> = {};

function buildLiveDocsUrl(workspaceId: string): string {
  return `/api/w/${workspaceId}/live-docs`;
}

function buildLiveDocTokenUrl(workspaceId: string, docId: string): string {
  return `${buildLiveDocsUrl(workspaceId)}/${docId}/token`;
}

export function useLiveDocConnection({
  owner,
  docId,
  disabled,
}: {
  owner: LightWorkspaceType;
  docId: string | undefined;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const tokenFetcher: Fetcher<GetLiveDocTokenResponseBody> = fetcher;

  const { data, error } = useSWRWithDefaults(
    docId ? buildLiveDocTokenUrl(owner.sId, docId) : null,
    tokenFetcher,
    { disabled: disabled || !docId, revalidateOnFocus: false }
  );

  return {
    connection: data ?? null,
    isLiveDocLoading: !disabled && !!docId && !error && !data,
    isLiveDocError: !!error,
  };
}

// The provider calls this on every (re)connect, so a reconnect after the 5-minute token expiry
// still authenticates.
export function useFetchLiveDocToken({
  owner,
  docId,
}: {
  owner: LightWorkspaceType;
  docId: string;
}) {
  return useCallback(async (): Promise<string> => {
    const res = await clientFetch(buildLiveDocTokenUrl(owner.sId, docId));
    if (!res.ok) {
      return "";
    }
    const body: GetLiveDocTokenResponseBody = await res.json();
    return body.token;
  }, [owner.sId, docId]);
}

export function useLiveDocs({
  owner,
  disabled,
}: {
  owner: LightWorkspaceType;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const docsFetcher: Fetcher<GetLiveDocsResponseBody> = fetcher;

  const { data, error, mutate } = useSWRWithDefaults(
    buildLiveDocsUrl(owner.sId),
    docsFetcher,
    { disabled }
  );

  return {
    liveDocs: data?.liveDocs ?? emptyArray<LiveDocType>(),
    summaries: data?.summaries ?? emptySummaries,
    isLiveDocsLoading: !disabled && !error && !data,
    isLiveDocsError: !!error,
    mutateLiveDocs: mutate,
  };
}

const emptyUrgency: Record<string, number> = {};

// Scores arrive after the lists render; until then, and whenever Jev has no opinion, the lists
// keep their own order.
export function useLiveDocUrgency({
  owner,
  docIds,
}: {
  owner: LightWorkspaceType;
  docIds: string[];
}) {
  const { fetcher } = useFetcher();
  const urgencyFetcher: Fetcher<{ urgency: Record<string, number> }> = fetcher;
  const key = [...new Set(docIds)].sort().join(",");

  const { data } = useSWRWithDefaults(
    key
      ? `${buildLiveDocsUrl(owner.sId)}/urgency?ids=${encodeURIComponent(key)}`
      : null,
    urgencyFetcher,
    { disabled: !key, revalidateOnFocus: false }
  );

  return data?.urgency ?? emptyUrgency;
}

// A conversation's space and its call are made the first time someone opens them; agents make
// them on first use. Either way it is one doc per conversation and kind.
export function useCreateConversationLiveDoc({
  owner,
}: {
  owner: LightWorkspaceType;
}) {
  const sendNotification = useSendNotification();
  const { mutateLiveDocs } = useLiveDocs({ owner, disabled: true });

  return useCallback(
    async ({
      conversationId,
      kind,
      title,
    }: {
      conversationId: string;
      kind: "space" | "media";
      title: string;
    }): Promise<LiveDocType | null> => {
      const res = await clientFetch(buildLiveDocsUrl(owner.sId), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ kind, conversationId, title }),
      });
      if (!res.ok) {
        sendNotification({
          type: "error",
          title:
            kind === "space"
              ? "Could not open the desktop"
              : "Could not start the huddle",
        });
        return null;
      }
      const body: { liveDoc: LiveDocType } = await res.json();
      await mutateLiveDocs();
      return body.liveDoc;
    },
    [owner.sId, sendNotification, mutateLiveDocs]
  );
}

export function useLiveDocHistory({
  owner,
  docId,
  disabled,
}: {
  owner: LightWorkspaceType;
  docId: string;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const historyFetcher: Fetcher<HistoryResponse> = fetcher;

  const { data, error, mutate } = useSWRWithDefaults(
    `${buildLiveDocsUrl(owner.sId)}/${docId}/history`,
    historyFetcher,
    { disabled }
  );

  return {
    history: data ?? null,
    isHistoryLoading: !disabled && !error && !data,
    isHistoryError: !!error,
    mutateHistory: mutate,
  };
}

export function useLiveBrowserFrames({
  owner,
  docId,
  disabled,
}: {
  owner: LightWorkspaceType;
  docId: string;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const framesFetcher: Fetcher<BrowserFramesResponse> = fetcher;

  const { data, error, mutate } = useSWRWithDefaults(
    `${buildLiveDocsUrl(owner.sId)}/${docId}/frames`,
    framesFetcher,
    { disabled }
  );

  return {
    frames:
      data?.frames ?? emptyArray<BrowserFramesResponse["frames"][number]>(),
    // An <img> carries the session cookie but not clientFetch's base URL, so it gets the
    // absolute address.
    frameUrl: (frameId: string) =>
      `${getBaseUrl()}${buildLiveDocsUrl(owner.sId)}/${docId}/frames/${frameId}`,
    isFramesLoading: !disabled && !error && !data,
    mutateFrames: mutate,
  };
}

// A session on the shared browser's own video server, minted when the viewer joins; one per mount.
export function useFetchLiveBrowserVideo({
  owner,
  docId,
}: {
  owner: LightWorkspaceType;
  docId: string;
}) {
  return useCallback(async (): Promise<BrowserVideoResponse | null> => {
    const res = await clientFetch(
      `${buildLiveDocsUrl(owner.sId)}/${docId}/browser-video`,
      { method: "POST" }
    );
    if (!res.ok) {
      return null;
    }
    const body: BrowserVideoResponse = await res.json();
    return body;
  }, [owner.sId, docId]);
}

// Minted on demand when the person joins the room; it is short-lived, so it is not cached.
export function useFetchLiveMediaToken({
  owner,
  docId,
  viewer,
}: {
  owner: LightWorkspaceType;
  docId: string;
  viewer?: string;
}) {
  return useCallback(async (): Promise<MediaTokenResponse | null> => {
    const res = await clientFetch(
      `${buildLiveDocsUrl(owner.sId)}/${docId}/media-token`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(viewer ? { viewer } : {}),
      }
    );
    if (!res.ok) {
      return null;
    }
    const body: MediaTokenResponse = await res.json();
    return body;
  }, [owner.sId, docId, viewer]);
}

export function usePostLiveTranscriptLine({
  owner,
  docId,
}: {
  owner: LightWorkspaceType;
  docId: string;
}) {
  return useCallback(
    async (line: {
      id: string;
      text: string;
      final: boolean;
    }): Promise<boolean> => {
      const res = await clientFetch(
        `${buildLiveDocsUrl(owner.sId)}/${docId}/transcript`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(line),
        }
      );
      return res.ok;
    },
    [owner.sId, docId]
  );
}

// Whether live-voice hears and speaks in calls; null while the answer is on its way.
export function useServerVoice({ owner }: { owner: LightWorkspaceType }) {
  const { fetcher } = useFetcher();
  const voiceFetcher: Fetcher<{ available: boolean }> = fetcher;
  const { data, error } = useSWRWithDefaults(
    `${buildLiveDocsUrl(owner.sId)}/voice`,
    voiceFetcher,
    { revalidateOnFocus: false }
  );
  return error ? false : (data?.available ?? null);
}

export function useLeaveLiveCall({
  owner,
  docId,
}: {
  owner: LightWorkspaceType;
  docId: string;
}) {
  return useCallback(
    (identity: string) => {
      void clientFetch(`${buildLiveDocsUrl(owner.sId)}/${docId}/left`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ identity }),
      });
    },
    [owner.sId, docId]
  );
}

const transcribeTokenSchema = z.object({
  token: z.string(),
  baseUri: z.string(),
});

// The composer's ElevenLabs token route serves the call's transcription too; a plan without it
// (BYOK) or a missing key answers non-OK, and the call falls back to the browser's speech service.
export function useFetchLiveTranscribeToken({
  owner,
}: {
  owner: LightWorkspaceType;
}) {
  return useCallback(async () => {
    const res = await clientFetch(
      `/api/w/${owner.sId}/services/transcribe/get-token`
    );
    if (!res.ok) {
      return null;
    }
    const parsed = transcribeTokenSchema.safeParse(await res.json());
    return parsed.success ? parsed.data : null;
  }, [owner.sId]);
}

export function useLiveDocVersion({
  owner,
  docId,
  uptoId,
  sinceId,
}: {
  owner: LightWorkspaceType;
  docId: string;
  uptoId: string | null;
  sinceId: string | null;
}) {
  const { fetcher } = useFetcher();
  const versionFetcher: Fetcher<VersionResponse> = fetcher;

  const { data, error } = useSWRWithDefaults(
    uptoId
      ? `${buildLiveDocsUrl(owner.sId)}/${docId}/versions/${uptoId}${sinceId ? `?since=${sinceId}` : ""}`
      : null,
    versionFetcher,
    { disabled: !uptoId, revalidateOnFocus: false }
  );

  return {
    version: data ?? null,
    isVersionLoading: !!uptoId && !error && !data,
  };
}

export function useRestoreLiveDoc({
  owner,
  docId,
}: {
  owner: LightWorkspaceType;
  docId: string;
}) {
  const sendNotification = useSendNotification();

  return useCallback(
    async (uptoId: string): Promise<boolean> => {
      const res = await clientFetch(
        `${buildLiveDocsUrl(owner.sId)}/${docId}/restore`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ uptoId }),
        }
      );
      sendNotification(
        res.ok
          ? { type: "success", title: "Version restored" }
          : { type: "error", title: "Could not restore this version" }
      );
      return res.ok;
    },
    [owner.sId, docId, sendNotification]
  );
}

// An @mention in a doc comment runs the agent in the doc's conversation. The server enables live
// docs there and posts the message, so the agent can answer in the thread right away.
export function usePostLiveDocMention({
  owner,
  docId,
}: {
  owner: LightWorkspaceType;
  docId: string;
}) {
  const sendNotification = useSendNotification();

  return useCallback(
    async (mention: {
      agentConfigurationId: string;
      threadId: string;
      blockId: string;
      quote: string;
      body: string;
    }): Promise<boolean> => {
      const res = await clientFetch(
        `${buildLiveDocsUrl(owner.sId)}/${docId}/mentions`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...mention,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          }),
        }
      );
      if (!res.ok) {
        sendNotification({
          type: "error",
          title: "The comment was saved, but the agent was not asked",
        });
      }
      return res.ok;
    },
    [owner.sId, docId, sendNotification]
  );
}

function buildLiveDocMembersUrl(workspaceId: string, docId: string): string {
  return `${buildLiveDocsUrl(workspaceId)}/${docId}/members`;
}

export function useLiveDocMembers({
  owner,
  docId,
  disabled,
}: {
  owner: LightWorkspaceType;
  docId: string;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const membersFetcher: Fetcher<{ members: LiveDocMemberType[] }> = fetcher;

  const { data, error, mutate } = useSWRWithDefaults(
    buildLiveDocMembersUrl(owner.sId, docId),
    membersFetcher,
    { disabled }
  );

  return {
    members: data?.members ?? emptyArray<LiveDocMemberType>(),
    isMembersLoading: !disabled && !error && !data,
    mutateMembers: mutate,
  };
}

export function useUpdateLiveDocMembers({
  owner,
  docId,
}: {
  owner: LightWorkspaceType;
  docId: string;
}) {
  const sendNotification = useSendNotification();
  const { mutateMembers } = useLiveDocMembers({ owner, docId, disabled: true });

  const send = useCallback(
    async (init: RequestInit, path = ""): Promise<boolean> => {
      const res = await clientFetch(
        `${buildLiveDocMembersUrl(owner.sId, docId)}${path}`,
        { ...init, headers: { "Content-Type": "application/json" } }
      );
      if (!res.ok) {
        sendNotification({
          type: "error",
          title: "Could not update who this doc is shared with",
        });
        return false;
      }
      await mutateMembers(await res.json());
      return true;
    },
    [owner.sId, docId, sendNotification, mutateMembers]
  );

  return {
    share: (userId: string, access: LiveDocAccess) =>
      send({ method: "POST", body: JSON.stringify({ userId, access }) }),
    unshare: (userId: string) =>
      send({ method: "DELETE" }, `/${encodeURIComponent(userId)}`),
  };
}

export function useCommentOnLiveDoc({
  owner,
  docId,
}: {
  owner: LightWorkspaceType;
  docId: string;
}) {
  const sendNotification = useSendNotification();

  return useCallback(
    async (
      request:
        | { threadId: string; body: string }
        | { blockId: string; quote: string; body: string }
    ): Promise<string | null> => {
      const res = await clientFetch(
        `${buildLiveDocsUrl(owner.sId)}/${docId}/comments`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(request),
        }
      );
      if (!res.ok) {
        sendNotification({
          type: "error",
          title: "Could not post the comment",
        });
        return null;
      }
      const body: { threadId: string } = await res.json();
      return body.threadId;
    },
    [owner.sId, docId, sendNotification]
  );
}

// Sheets download as Excel and decks as PowerPoint, both built by live-server from the live state.
export function useDownloadLiveDocFile({
  owner,
  docId,
  format,
}: {
  owner: LightWorkspaceType;
  docId: string;
  format: "xlsx" | "pptx";
}) {
  const sendNotification = useSendNotification();

  return useCallback(
    async (title: string): Promise<void> => {
      const res = await clientFetch(
        `${buildLiveDocsUrl(owner.sId)}/${docId}/${format}`
      );
      if (!res.ok) {
        sendNotification({
          type: "error",
          title: `Could not export ${format === "xlsx" ? "the sheet" : "the slides"}`,
        });
        return;
      }
      const link = document.createElement("a");
      link.href = URL.createObjectURL(await res.blob());
      link.download = `${title}.${format}`;
      link.click();
      URL.revokeObjectURL(link.href);
    },
    [owner.sId, docId, format, sendNotification]
  );
}

// A Frame reads a sheet as records keyed by its header row; blank headers fall back to the column
// letter so no value is dropped.
export function useReadLiveSheetForFrame({
  workspaceId,
}: {
  workspaceId: string;
}) {
  return useCallback(
    async (docId: string, range?: string): Promise<FrameLiveSheet | null> => {
      const res = await clientFetch(
        `${buildLiveDocsUrl(workspaceId)}/${docId}/sheet${range ? `?range=${encodeURIComponent(range)}` : ""}`
      );
      if (!res.ok) {
        return null;
      }
      const body: SheetReadResponse & { title: string } = await res.json();
      const values = body.rows.map((row) =>
        row.cells.map((cell) => cell.value)
      );
      const [headerRow, ...dataRows] = body.rows;
      const headers = (headerRow?.cells ?? []).map((cell, i) =>
        typeof cell.value === "string" && cell.value
          ? cell.value
          : (body.columns[i]?.column ?? String(i))
      );
      return {
        docId,
        title: body.title,
        headers,
        rows: dataRows.map((row) =>
          Object.fromEntries(
            row.cells.map((cell, i) => [headers[i], cell.value])
          )
        ),
        values,
      };
    },
    [workspaceId]
  );
}

const runnerRefusalSchema = z.object({
  error: z.object({ message: z.string() }).optional(),
});

async function runnerCommand(
  url: string,
  body: object
): Promise<string | null> {
  const res = await clientFetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (res.ok) {
    return null;
  }
  const refusal = runnerRefusalSchema.safeParse(
    await res.json().catch(() => null)
  );
  return (
    (refusal.success ? refusal.data.error?.message : undefined) ??
    "The runner did not answer."
  );
}

// A runner window's controls. Its files are served from front-api to a sandboxed frame, under a
// token fetched again with every run so the URLs stay fresh; relative links in a dashboard
// resolve against the same prefix.
export function useLiveRunner({
  owner,
  docId,
  runKey,
}: {
  owner: LightWorkspaceType;
  docId: string;
  runKey: string | null;
}): LiveHostRunner | null {
  const { fetcher } = useFetcher();
  const runnerFetcher: Fetcher<{ frameToken: string }> = fetcher;
  const url = `${buildLiveDocsUrl(owner.sId)}/${docId}/runner`;
  const { data, error } = useSWRWithDefaults(
    `${url}?run=${encodeURIComponent(runKey ?? "")}`,
    runnerFetcher,
    { revalidateOnFocus: false }
  );
  return useMemo(() => {
    if (error) {
      return {
        available: false,
        outputUrl: () => "",
        dashboardUrl: null,
        rerun: async () => "This runner is not available to you.",
        setPaused: async () => "This runner is not available to you.",
      };
    }
    if (!data) {
      return null;
    }
    const outputUrl = (name: string) =>
      `${getBaseUrl()}/api/live/runner-frame/${encodeURIComponent(docId)}/${encodeURIComponent(data.frameToken)}/${encodeURIComponent(name)}`;
    return {
      available: true,
      outputUrl,
      dashboardUrl: outputUrl("index.html"),
      rerun: () => runnerCommand(`${url}/run`, {}),
      setPaused: (paused: boolean) => runnerCommand(`${url}/pause`, { paused }),
    };
  }, [data, error, docId, url]);
}

const dockLaunchers: { kind: LiveDocKind; label: string; title: string }[] = [
  { kind: "doc", label: "Doc", title: "Untitled doc" },
  { kind: "sheet", label: "Sheet", title: "Untitled sheet" },
  { kind: "canvas", label: "Canvas", title: "Untitled canvas" },
  { kind: "slides", label: "Slides", title: "Untitled slides" },
  { kind: "browser", label: "Browser", title: "Browser" },
  { kind: "media", label: "Call", title: "Call" },
];

// A conversation's space drawn as a desktop: each launcher makes a live object in the space's
// conversation, and the space places it like any object nobody placed yet.
// ponytail: no Upload launcher; uploaded files need a "file" object and useFile in Memoair's host.
export function useLiveDock({
  owner,
  space,
  onCall,
}: {
  owner: LightWorkspaceType;
  space: LiveHostDoc | null;
  // Opens the conversation's huddle; without it the Call launcher places a call window.
  onCall:
    | ((conversationId: string, title: string | null) => Promise<void>)
    | null;
}): LiveDockItem[] | null {
  const sendNotification = useSendNotification();
  const { mutateLiveDocs } = useLiveDocs({ owner, disabled: true });
  const conversationId = space?.conversationId ?? null;

  return useMemo(() => {
    if (!conversationId) {
      return null;
    }
    const create = async (kind: LiveDocKind, title: string) => {
      const res = await clientFetch(buildLiveDocsUrl(owner.sId), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ kind, conversationId, title }),
      });
      if (!res.ok) {
        sendNotification({
          type: "error",
          title: `Could not add the ${title.toLowerCase()}`,
        });
        return;
      }
      await mutateLiveDocs();
    };
    return dockLaunchers.map(({ kind, label, title }) => ({
      kind,
      label,
      onOpen: () =>
        void (kind === "media" && onCall
          ? onCall(conversationId, null)
          : create(kind, title)),
    }));
  }, [conversationId, owner.sId, sendNotification, mutateLiveDocs, onCall]);
}

const huddleLiveRefreshMs = 10_000;
const huddleNotesRefreshMs = 3_000;
const emptyHuddles: LiveHuddleType[] = [];

// The conversation's huddles, oldest first, for their cards in the chat.
// ponytail: refreshed every 10 s while one is live, so its card turns into the ended card; push it
// through the conversation's event stream if that delay shows.
export function useConversationHuddles({
  owner,
  conversationId,
}: {
  owner: LightWorkspaceType;
  conversationId: string | null;
}) {
  const { fetcher } = useFetcher();
  const huddlesFetcher: Fetcher<{ huddles: LiveHuddleType[] }> = fetcher;
  const { data, mutate } = useSWRWithDefaults(
    conversationId
      ? `${buildLiveDocsUrl(owner.sId)}/huddles?conversationId=${encodeURIComponent(conversationId)}`
      : null,
    huddlesFetcher,
    {
      disabled: !conversationId,
      // ponytail: polls only while a huddle is live or its notes are being written; a push from
      // live-server on huddle changes would replace it.
      refreshInterval: (latest: { huddles: LiveHuddleType[] } | undefined) =>
        latest?.huddles.some((huddle) =>
          isHuddleWritingNotes(huddle, Date.now())
        )
          ? huddleNotesRefreshMs
          : latest?.huddles.some((huddle) => huddle.endedAt === null)
            ? huddleLiveRefreshMs
            : 0,
    }
  );
  return { huddles: data?.huddles ?? emptyHuddles, mutateHuddles: mutate };
}
